import { supabase, uploadFileToStorage } from './supabase';

const BUCKET_NAME = import.meta.env.VITE_STORAGE_BUCKET || 'location-images';
const VALID_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const validateImageFile = (file: File): void => {
  if (!file || !(file instanceof File)) {
    throw new Error('Невалідны файл');
  }

  if (!VALID_MIME_TYPES.includes(file.type)) {
    throw new Error('Недапушчальны тып файла. Дазволены толькі JPEG, PNG, GIF і WebP.');
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error('Файл занадта вялікі. Максімальны памер - 5MB.');
  }
};

export const getImageUrl = (imagePath: string): string => {
  if (!imagePath || typeof imagePath !== 'string') {
    console.warn('Invalid image path provided to getImageUrl:', imagePath);
    return '';
  }

  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  try {
    const { data } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(imagePath);
    
    console.log('getImageUrl: Generated public URL for', imagePath, '->', data.publicUrl);
    return data.publicUrl;
  } catch (error) {
    console.error('Error generating public URL in getImageUrl:', error);
    return '';
  }
};

// Upload image for regular users (requires authentication)
export const uploadImage = async (file: File): Promise<string> => {
  try {
    validateImageFile(file);

    console.log('Starting authenticated user upload...');
    console.log('File details:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${timestamp}_${random}.${ext}`;

    console.log('Generated filename:', fileName);

    // Use unified upload function with authentication required
    const publicUrl = await uploadFileToStorage(BUCKET_NAME, fileName, file, true);
    
    console.log('Upload successful, public URL:', publicUrl);
    return publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

// Upload image for admin (still requires authentication but different naming)
export const uploadImageAdmin = async (file: File): Promise<string> => {
  try {
    validateImageFile(file);

    console.log('Starting admin upload...');
    console.log('File details:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `admin_${timestamp}_${random}.${ext}`;

    console.log('Generated admin filename:', fileName);

    // Use unified upload function with authentication required
    // Admin uploads still need authentication in Supabase
    const publicUrl = await uploadFileToStorage(BUCKET_NAME, fileName, file, true);
    
    console.log('Admin upload successful, public URL:', publicUrl);
    return publicUrl;
  } catch (error) {
    console.error('Error uploading admin image:', error);
    throw error;
  }
};

export const uploadImages = async (files: File[]): Promise<string[]> => {
  try {
    console.log('Starting batch upload of', files.length, 'images');
    
    // Upload files sequentially to avoid overwhelming the server
    const results: string[] = [];
    for (let i = 0; i < files.length; i++) {
      console.log(`Uploading image ${i + 1} of ${files.length}`);
      const url = await uploadImage(files[i]);
      
      // Validate that we got a proper URL
      if (!url || !url.startsWith('http')) {
        throw new Error(`Невалідны URL для файла ${files[i].name}: ${url}`);
      }
      
      results.push(url);
      
      // Small delay between uploads to avoid rate limiting
      if (i < files.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    
    console.log('All images uploaded successfully:', results);
    return results;
  } catch (error) {
    console.error('Error uploading images:', error);
    throw error;
  }
};

export const uploadImagesAdmin = async (files: File[]): Promise<string[]> => {
  try {
    console.log('Starting admin batch upload of', files.length, 'images');
    
    // Upload files sequentially to avoid overwhelming the server
    const results: string[] = [];
    for (let i = 0; i < files.length; i++) {
      console.log(`Admin uploading image ${i + 1} of ${files.length}`);
      const url = await uploadImageAdmin(files[i]);
      
      // Validate that we got a proper URL
      if (!url || !url.startsWith('http')) {
        throw new Error(`Невалідны URL для файла ${files[i].name}: ${url}`);
      }
      
      results.push(url);
      
      // Small delay between uploads to avoid rate limiting
      if (i < files.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    
    console.log('All admin images uploaded successfully:', results);
    return results;
  } catch (error) {
    console.error('Error uploading admin images:', error);
    throw error;
  }
};